<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center flex-wrap ga-2 mb-4">
      <h2>Rapportagecategorieën</h2>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Categorie toevoegen
      </v-btn>
    </div>

    <!-- Loading State -->
    <v-progress-linear v-if="store.loading" indeterminate></v-progress-linear>

    <!-- Error State -->
    <v-alert v-if="store.error" type="error" class="mb-4">
      {{ store.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card
      v-if="!store.loading && store.reportCategories.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4"
        >mdi-folder-outline</v-icon
      >
      <h3 class="text-h6 mb-2">Nog geen rapportagecategorieën</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Maak de eerste categorie aan om rapporten in te delen.
      </p>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Categorie toevoegen
      </v-btn>
    </v-card>

    <!-- Report Categories List -->
    <v-card v-if="!store.loading && store.reportCategories.length > 0">
      <v-list>
        <v-list-item
          v-for="category in store.reportCategories"
          :key="category.id"
          class="border-b"
        >
          <template #prepend>
            <v-icon :color="category.color || 'primary'">
              {{ category.icon || "mdi-folder" }}
            </v-icon>
          </template>

          <div class="d-flex flex-column">
            <v-list-item-title>{{ category.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="chipsFor(category).length" class="mt-1">
              <div class="d-flex flex-wrap ga-1">
                <v-chip
                  v-for="chip in chipsFor(category)"
                  :key="chip.label"
                  size="x-small"
                  variant="tonal"
                >
                  <v-icon start size="12">{{ chip.icon }}</v-icon>
                  {{ chip.label }}
                </v-chip>
              </div>
            </v-list-item-subtitle>
          </div>

          <template #append>
            <div class="d-flex ga-2">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                :aria-label="`${category.name} bewerken`"
                @click="openEditDialog(category)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                :aria-label="`${category.name} verwijderen`"
                @click="confirmDelete(category)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog
      v-model="dialog"
      max-width="720px"
      persistent
      scrollable
      :fullscreen="xs"
    >
      <v-card :class="{ 'category-dialog': !xs }">
        <!-- Title with a live preview of how the category will look -->
        <v-card-title class="d-flex align-center flex-wrap ga-3 pb-0">
          <span>{{
            isEditing ? "Categorie bewerken" : "Categorie toevoegen"
          }}</span>
          <v-chip variant="outlined" size="small" class="category-preview">
            <v-icon start :color="formData.color || 'primary'">
              {{ formData.icon || "mdi-folder" }}
            </v-icon>
            {{ formData.name || "Naam van de categorie" }}
          </v-chip>
        </v-card-title>

        <v-tabs v-model="tab" color="primary" density="comfortable">
          <v-tab v-for="item in tabs" :key="item.value" :value="item.value">
            {{ item.title }}
            <v-icon
              v-if="item.invalid"
              end
              size="16"
              color="error"
              aria-label="Bevat een fout"
              >mdi-alert-circle</v-icon
            >
            <v-icon
              v-else-if="item.filled"
              end
              size="10"
              color="primary"
              aria-label="Ingevuld"
              >mdi-circle</v-icon
            >
          </v-tab>
        </v-tabs>
        <v-divider />

        <v-card-text class="pt-4">
          <v-form ref="formRef" v-model="formValid">
            <!-- eager: every field exists, so validation covers all tabs -->
            <v-window v-model="tab" class="form-window">
              <v-window-item value="general" eager>
                <v-text-field
                  v-model="formData.name"
                  label="Naam"
                  :rules="nameRules"
                  variant="outlined"
                  density="comfortable"
                  required
                  autofocus
                  class="mb-2"
                />

                <div class="section-title">Weergave</div>
                <ColorPickerField
                  v-model="formData.color"
                  label="Kleur"
                  variant="outlined"
                  density="comfortable"
                />
                <IconPickerField
                  v-model="formData.icon"
                  label="Icoon (bijv. mdi-star)"
                  density="comfortable"
                  grid-label="Of kies een veelgebruikt icoon:"
                  :preview-color="formData.color || 'primary'"
                />

                <div class="section-title mt-5">
                  Wat een rapport van dit type doet
                </div>
                <v-list density="compact" class="behaviour-list pa-0">
                  <v-list-item
                    v-for="behaviour in BEHAVIOURS"
                    :key="behaviour.field"
                    class="px-0"
                    @click="
                      formData[behaviour.field] = !formData[behaviour.field]
                    "
                  >
                    <template #prepend>
                      <v-icon>{{ behaviour.icon }}</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">
                      {{ behaviour.title }}
                    </v-list-item-title>
                    <div class="text-caption text-medium-emphasis">
                      {{ behaviour.description }}
                    </div>
                    <template #append>
                      <v-switch
                        v-model="formData[behaviour.field]"
                        color="primary"
                        hide-details
                        density="compact"
                        :aria-label="behaviour.title"
                        @click.stop
                      />
                    </template>
                  </v-list-item>
                </v-list>
              </v-window-item>

              <v-window-item value="quick_action" eager>
                <p class="text-body-2 text-medium-emphasis mb-4">
                  Een snelle actie is een knop bij een persoon waarmee je in één
                  klik een rapport van dit type maakt, met de tekst hieronder al
                  ingevuld. Laat de naam leeg als dit type geen snelle actie
                  heeft.
                </p>
                <v-text-field
                  v-model="formData.quick_action_name"
                  label="Naam op de knop"
                  placeholder="Bijv. Gezien"
                  :rules="quickActionNameRules"
                  counter="20"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-lightning-bolt"
                  clearable
                  class="mb-2"
                />
                <div class="text-body-2 mb-1">Tekst van het rapport</div>
                <div class="border rounded pa-2">
                  <AtMentionBox
                    v-model="formData.quick_action_template"
                    :instance_id="formData.id || 0"
                  />
                </div>
              </v-window-item>

              <v-window-item value="ai" eager>
                <p class="text-body-2 text-medium-emphasis mb-4">
                  Beide velden zijn optioneel en worden alleen door de
                  AI-assistent gebruikt.
                </p>
                <v-textarea
                  v-model="formData.ai_meaning"
                  label="Betekenis van dit type"
                  hint="Wat zegt een rapport van dit type? De AI krijgt dit als uitleg bij de samenvatting van een persoon."
                  placeholder="Bijv. De persoon is gezien op deze locatie; er is niet gesproken."
                  persistent-hint
                  persistent-placeholder
                  variant="outlined"
                  auto-grow
                  rows="2"
                  counter="500"
                  :rules="aiMeaningRules"
                  class="mb-4"
                />
                <v-textarea
                  v-model="formData.ai_guidance"
                  label="Instructies bij het schrijven"
                  :hint="`Waar 'Tekst controleren' op let bij een rapport van dit type. ${CHECK_SYNTAX_HINT}`"
                  placeholder="[belangrijk] Er staat met welke instantie er contact was.&#10;[urgent] Geen achternamen van derden."
                  persistent-hint
                  persistent-placeholder
                  variant="outlined"
                  auto-grow
                  rows="3"
                  counter="2000"
                  :rules="aiGuidanceRules"
                />
              </v-window-item>
            </v-window>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Annuleren</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            :disabled="!formValid"
            @click="save"
          >
            Opslaan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Categorie verwijderen?</v-card-title>
        <v-card-text>
          "{{ categoryToDelete?.name }}" wordt verwijderd. Dit kan niet ongedaan
          worden gemaakt.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Annuleren</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteCategory">
            Verwijderen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
/**
 * Admin screen for report categories (report types). The form is split in three
 * tabs: what the type is and does, its quick action, and what the AI assistant
 * should know about it. A tab shows a dot when it holds something and a warning
 * when one of its fields is invalid, so nothing is hidden behind a tab.
 */
import { ref, reactive, computed, onMounted } from "vue";
import { useDisplay } from "vuetify";
import { useReportCategoriesStoreRefactor } from "@/stores/reportCategoriesStoreRefactor";
import AtMentionBox from "@/components/ui/AtMentionBox.vue";
import ColorPickerField from "@/components/ui/ColorPickerField.vue";
import IconPickerField from "@/components/ui/IconPickerField.vue";
import {
  CATEGORY_LIMITS,
  categoryChips,
  categoryFormTabs,
} from "@/utils/reportCategoryForm";
import { CHECK_SYNTAX_HINT } from "@/utils/aiFeedback";

// What a report of this type does in the app, besides being a report.
const BEHAVIOURS = [
  {
    field: "visual",
    icon: "mdi-eye",
    title: "Telt als gezien",
    description:
      "Werkt 'laatst gezien' van de persoon bij. Gebruik dit voor een waarneming zonder gesprek.",
  },
  {
    field: "repat",
    icon: "mdi-airplane-takeoff",
    title: "Telt als vertrek",
    description:
      "Markeert de persoon als vertrokken (repatriëring) en legt de vertrekdatum vast.",
  },
];

const store = useReportCategoriesStoreRefactor();
const { xs } = useDisplay();

const dialog = ref(false);
const deleteDialog = ref(false);
const formRef = ref(null);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isEditing = ref(false);
const categoryToDelete = ref(null);
const tab = ref("general");

const emptyForm = () => ({
  id: null,
  name: "",
  repat: false,
  visual: false,
  icon: "",
  color: "",
  quick_action_name: "",
  quick_action_template: "",
  ai_guidance: "",
  ai_meaning: "",
});
const formData = reactive(emptyForm());

const tabs = computed(() => categoryFormTabs(formData));
const chipsFor = (category) => categoryChips(category);

const maxLength = (limit) => (value) =>
  !value || value.length <= limit || `Maximaal ${limit} tekens`;
const nameRules = [
  (value) => !!(value && value.trim()) || "Vul een naam in",
  maxLength(CATEGORY_LIMITS.name),
];
const quickActionNameRules = [maxLength(CATEGORY_LIMITS.quick_action_name)];
const aiMeaningRules = [maxLength(CATEGORY_LIMITS.ai_meaning)];
const aiGuidanceRules = [maxLength(CATEGORY_LIMITS.ai_guidance)];

const openCreateDialog = () => {
  isEditing.value = false;
  Object.assign(formData, emptyForm());
  tab.value = "general";
  dialog.value = true;
};

const openEditDialog = (category) => {
  isEditing.value = true;
  Object.assign(formData, emptyForm(), {
    id: category.id,
    name: category.name,
    repat: category.repat || false,
    visual: category.visual || false,
    icon: category.icon || "",
    color: category.color || "",
    quick_action_name: category.quick_action_name || "",
    quick_action_template: category.quick_action_template || "",
    ai_guidance: category.ai_guidance || "",
    ai_meaning: category.ai_meaning || "",
  });
  tab.value = "general";
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
};

const save = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    // Show the first tab that has a problem
    tab.value = tabs.value.find((item) => item.invalid)?.value || tab.value;
    return;
  }

  saving.value = true;

  try {
    const categoryData = {
      name: formData.name.trim(),
      repat: formData.repat,
      visual: formData.visual,
      icon: formData.icon,
      color: formData.color,
      quick_action_name: formData.quick_action_name,
      quick_action_template: formData.quick_action_template,
      ai_guidance: formData.ai_guidance || null,
      ai_meaning: formData.ai_meaning || null,
    };

    if (isEditing.value) {
      await store.update({ id: formData.id, ...categoryData });
    } else {
      await store.create(categoryData);
    }

    closeDialog();
  } catch (error) {
    console.error("Error saving category:", error);
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (category) => {
  categoryToDelete.value = category;
  deleteDialog.value = true;
};

const deleteCategory = async () => {
  if (!categoryToDelete.value) return;

  deleting.value = true;

  try {
    await store.destroy(categoryToDelete.value.id);
    deleteDialog.value = false;
    categoryToDelete.value = null;
  } catch (error) {
    console.error("Error deleting category:", error);
  } finally {
    deleting.value = false;
  }
};

// Load data on mount
onMounted(async () => {
  await store.fetchAll();
});
</script>

<style scoped>
.category-preview {
  max-width: 100%;
}

/* One size for every tab, so the dialog does not jump when switching. */
.category-dialog {
  height: 820px;
  max-height: 90vh;
}

.form-window {
  /* room for the floating label of the first outlined field */
  padding-top: 8px;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 12px;
}

.behaviour-list :deep(.v-list-item) {
  min-height: 56px;
  /* keep the switch clear of the dialog's edge */
  padding-inline-end: 8px !important;
}
</style>
